/**
 * `useTableStateChrome` — the preset's loading/empty/error presentation layer: maps the
 * derived `TableState` to overlay nodes, `aria-busy`, the `aria-live` messages, and the
 * retry handler. Owns only WHERE each overlay renders relative to the grid.
 *
 * `standaloneOverlay` REPLACES the body (skeleton/empty/error) and carries non-row
 * children (the error Retry `<button>`), so the caller renders it as a DOM SIBLING of the
 * grid — a `<button>` under `role="grid"` is an `aria-required-children` axe violation.
 * `refetchOverlay` rides on top of the still-mounted body during a keepPreviousData
 * refetch — also a grid sibling, absolutely positioned on the card (see `RefetchOverlay`
 * for why not sticky), `aria-hidden` with no interactive children. A consumer
 * `renderState` overrides the machine's overlay in every state.
 */
import type { ReactNode } from 'react'
import { useEvent } from 'react-use-event-hook'

import {
  type DataTableAnnouncements,
  type DataTableQueryState,
  type DataTableState,
  type TableAnnouncements,
  useTableAnnouncements,
} from './primitives/state-machine'
import {
  type DataTableStateCopy,
  EmptyState,
  ErrorState,
  InitialLoadingState,
  RefetchOverlay,
} from './primitives/states'

export type UseTableStateChromeOptions = {
  /** The derived state (from `deriveTableState`) — the machine output this layer renders. */
  tableState: DataTableState
  query: DataTableQueryState | undefined
  stateCopy: DataTableStateCopy | undefined
  announcements: DataTableAnnouncements | undefined
  /** Consumer-supplied overlay chrome that OVERRIDES the machine's overlay when set. */
  renderState: ReactNode
  hasAnyData: boolean
  /** Loaded (logical) row count — feeds the "Table loaded" announcement. */
  rowCount: number
  /** Exact selected count, or `undefined` for an unresolved-total "all matching". */
  selectedCountExact: number | undefined
  /** Sticky-header height (px), so the refetch bar rides UNDER the header, not across it. */
  headerHeightPx: number
}

export type TableStateChrome = {
  /** Body-replacing overlay (skeleton / empty / error / `renderState`) — a grid SIBLING. */
  standaloneOverlay: ReactNode
  /** Non-blocking refetch overlay riding on top of the still-mounted body. */
  refetchOverlay: ReactNode
  isBusy: boolean
  liveMessages: TableAnnouncements
  /** Retry the failed next-page fetch (inline retry, rendered by the caller). */
  handleRetryLoadMore: () => void
}

export const useTableStateChrome = (options: UseTableStateChromeOptions): TableStateChrome => {
  const {
    tableState,
    query,
    stateCopy,
    announcements,
    renderState,
    hasAnyData,
    rowCount,
    selectedCountExact,
    headerHeightPx,
  } = options

  // Event-time retries → `useEvent`; each reads the live `query` callback at call time.
  const handleRetry = useEvent(() => query?.refetch?.())
  const handleRetryLoadMore = useEvent(() => query?.retryLoadMore?.())

  // `selectedCountExact` must be the exact count or `undefined` (unresolved "all
  // matching") — never a degraded 0, which would announce "0 selected" for select-all.
  const liveMessages = useTableAnnouncements({
    state: tableState,
    rowCount,
    selectedCountExact,
    announcements,
  })

  const standaloneOverlay: ReactNode = (() => {
    if (renderState !== undefined) {
      return renderState
    }
    if (tableState.kind === 'initialLoading') {
      return <InitialLoadingState />
    }
    if (tableState.kind === 'error') {
      return (
        <ErrorState
          copy={stateCopy}
          onRetry={query?.refetch === undefined ? undefined : handleRetry}
        />
      )
    }
    if (tableState.kind === 'empty') {
      return (
        <EmptyState
          copy={stateCopy}
          hasAnyData={hasAnyData}
        />
      )
    }

    return null
  })()

  // Non-blocking refetch overlay; suppressed when `renderState` owns the overlay surface.
  const refetchOverlay: ReactNode =
    renderState === undefined && tableState.kind === 'populated' && tableState.isRefetching ? (
      <RefetchOverlay topOffset={headerHeightPx} />
    ) : null

  // `aria-busy` carries no message, so it is PAIRED with the live region, never a substitute.
  const isBusy = tableState.kind === 'initialLoading' || tableState.isRefetching

  return { standaloneOverlay, refetchOverlay, isBusy, liveMessages, handleRetryLoadMore }
}
